import React, { useState } from 'react';
import Header from '../components/Header';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { MessageSquare, Star } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const StudentSurvey = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    class: '',
    email: '',
    teachingQuality: '',
    infrastructure: '',
    libraryFacilities: '',
    examSystem: '',
    overallSatisfaction: '',
    suggestions: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    toast({
      title: "Survey Submitted!",
      description: "Thank you for your valuable feedback.",
    });
    // Reset form
    setFormData({
      name: '',
      class: '',
      email: '',
      teachingQuality: '',
      infrastructure: '',
      libraryFacilities: '',
      examSystem: '',
      overallSatisfaction: '',
      suggestions: ''
    });
  };

  const ratings = [1, 2, 3, 4, 5];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <Navigation />
      
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-[#000066] mb-2 flex items-center gap-3">
              <MessageSquare className="w-10 h-10" />
              Student Satisfaction Survey
            </h1>
            <p className="text-gray-600">Your feedback helps us improve our services</p>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-8">
            <form onSubmit={handleSubmit}>
              {/* Basic Information */}
              <div className="mb-6">
                <h2 className="text-xl font-bold text-[#000066] mb-4">Basic Information</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#000066] focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Class
                    </label>
                    <input
                      type="text"
                      name="class"
                      value={formData.class}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#000066] focus:border-transparent"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#000066] focus:border-transparent"
                  />
                </div>
              </div>

              {/* Rating Questions */}
              <div className="mb-6">
                <h2 className="text-xl font-bold text-[#000066] mb-4">Rate Your Experience</h2>
                
                {/* Teaching Quality */}
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Teaching Quality
                  </label>
                  <div className="flex gap-2">
                    {ratings.map((rating) => (
                      <button
                        key={rating}
                        type="button"
                        onClick={() => setFormData({ ...formData, teachingQuality: rating.toString() })}
                        className={`px-4 py-2 rounded transition-colors ${
                          formData.teachingQuality === rating.toString()
                            ? 'bg-[#FF4500] text-white'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        {rating} <Star className="w-4 h-4 inline" />
                      </button>
                    ))}
                  </div>
                </div>

                {/* Infrastructure */}
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Infrastructure Facilities
                  </label>
                  <div className="flex gap-2">
                    {ratings.map((rating) => (
                      <button
                        key={rating}
                        type="button"
                        onClick={() => setFormData({ ...formData, infrastructure: rating.toString() })}
                        className={`px-4 py-2 rounded transition-colors ${
                          formData.infrastructure === rating.toString()
                            ? 'bg-[#FF4500] text-white'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        {rating} <Star className="w-4 h-4 inline" />
                      </button>
                    ))}
                  </div>
                </div>

                {/* Library */}
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Library Facilities
                  </label>
                  <div className="flex gap-2">
                    {ratings.map((rating) => (
                      <button
                        key={rating}
                        type="button"
                        onClick={() => setFormData({ ...formData, libraryFacilities: rating.toString() })}
                        className={`px-4 py-2 rounded transition-colors ${
                          formData.libraryFacilities === rating.toString()
                            ? 'bg-[#FF4500] text-white'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        {rating} <Star className="w-4 h-4 inline" />
                      </button>
                    ))}
                  </div>
                </div>

                {/* Exam System */}
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Examination System
                  </label>
                  <div className="flex gap-2">
                    {ratings.map((rating) => (
                      <button
                        key={rating}
                        type="button"
                        onClick={() => setFormData({ ...formData, examSystem: rating.toString() })}
                        className={`px-4 py-2 rounded transition-colors ${
                          formData.examSystem === rating.toString()
                            ? 'bg-[#FF4500] text-white'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        {rating} <Star className="w-4 h-4 inline" />
                      </button>
                    ))}
                  </div>
                </div>

                {/* Overall */}
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Overall Satisfaction
                  </label>
                  <div className="flex gap-2">
                    {ratings.map((rating) => (
                      <button
                        key={rating}
                        type="button"
                        onClick={() => setFormData({ ...formData, overallSatisfaction: rating.toString() })}
                        className={`px-4 py-2 rounded transition-colors ${
                          formData.overallSatisfaction === rating.toString()
                            ? 'bg-[#FF4500] text-white'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        {rating} <Star className="w-4 h-4 inline" />
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Suggestions */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Suggestions for Improvement
                </label>
                <textarea
                  name="suggestions"
                  value={formData.suggestions}
                  onChange={handleChange}
                  rows="4"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#000066] focus:border-transparent"
                  placeholder="Please share your suggestions..."
                />
              </div>

              <button
                type="submit"
                className="w-full bg-[#000066] text-white py-3 rounded-lg hover:bg-[#000099] transition-colors font-semibold"
              >
                Submit Survey
              </button>
            </form>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default StudentSurvey;